import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Copy, Loader2, Search, Trash2, UserCheck } from "lucide-react";
import { EmptyState } from "./common";
import { StaffName } from "./StaffAvatar";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { api, errMsg, fmtDate, fmtDateTime, fmtINR } from "../lib/api";
import { pokeLiveCollections } from "../lib/liveCollections";

/**
 * Phase 135 — Duplicate imported payments.
 * A duplicate is a bulk-imported payment with the same PIN + date + receipt number (normalised: case, separators
 * and leading zeros ignored) + amount as a payment recorded through the app. The APP record is always kept;
 * only the import copy is removed — its money goes back on the demands / advance credit is taken back, the
 * payment, receipt and auto-logged visit move to the recycle bin, and every removal is audited.
 */
export function PaymentDuplicatesPanel({ code, testId = "payment-duplicates" }) {
  return (
    <div className="space-y-4" data-testid={`${testId}-panel`}>
      <ImportCreditCard code={code} testId="import-credit" />
      <DuplicatesCard code={code} testId={testId} />
    </div>
  );
}

/**
 * Phase 136 — staff credit for imported receipts. Receipts imported before the credit rule existed (or on properties
 * that had nobody assigned at the time) carry no collector; this credits them to the property's CURRENT assigned
 * Tax Collector (else Supervisor) so Monthly Performance, the Leaderboard and Field Activity count them.
 */
function ImportCreditCard({ code, testId }) {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const load = useCallback(() => {
    api.get(`/t/${code}/payment-import/recredit/status`).then((r) => setStatus(r.data)).catch(() => {});
  }, [code]);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/t/${code}/payment-import/recredit`, { recheck: true }, { timeout: 300000 });
      setResult(r.data);
      if (r.data.credited) toast.success(`${r.data.credited} imported receipt(s) credited — ${fmtINR(r.data.credited_amount || 0)}`);
      else toast.info(r.data.pending ? "No assigned collector or supervisor found for the remaining receipts" : "Every imported receipt already carries its staff credit");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };
  const lastRun = status?.runs?.[0];
  return (
    <Card className="shadow-sm" data-testid={`${testId}-card`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center gap-2"><UserCheck className="h-4 w-4" /> Staff credit for imported receipts</CardTitle>
        <p className="text-xs text-muted-foreground">
          Every imported receipt is credited to the property's <b>assigned Tax Collector</b> (or its Supervisor when no collector is mapped), so
          Monthly Performance, the Collector Leaderboard, Daily Field Activity and the dashboards total the <b>same payments table</b> as the Collections
          register. This runs automatically; use it again after mapping collectors to properties that had nobody assigned.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" onClick={run} disabled={running} data-testid={`${testId}-run-button`}>
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCheck className="h-4 w-4 mr-2" />} Credit to assigned staff now
          </Button>
          {status && (
            <p className="text-xs text-muted-foreground tabular-nums" data-testid={`${testId}-status`}>
              <b className="text-foreground">{(status.uncredited || 0).toLocaleString("en-IN")}</b> imported receipt(s) still without staff credit
              {status.uncredited > 0 ? " (no assigned collector or supervisor on their properties)" : ""}
              {lastRun ? ` · last run ${fmtDateTime(lastRun.started_at)}: ${lastRun.credited} credited (${fmtINR(lastRun.credited_amount || 0)})` : ""}
            </p>
          )}
        </div>
        {result && (
          <Alert className="border-primary/30 bg-primary/5" data-testid={`${testId}-result`}>
            <CheckCircle2 className="h-4 w-4" /><AlertTitle>Run complete</AlertTitle>
            <AlertDescription className="text-xs tabular-nums">
              {result.pending} receipt(s) checked · <b>{result.credited}</b> credited ({fmtINR(result.credited_amount || 0)}) · {result.no_staff} left without assigned staff
              {result.visits_logged ? ` · ${result.visits_logged} collection visit(s) logged` : ""}.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function DuplicatesCard({ code, testId }) {
  const [scan, setScan] = useState(null);        // {scanned, duplicates, amount, count, truncated}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [typed, setTyped] = useState("");
  const [removing, setRemoving] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [runs, setRuns] = useState([]);

  const loadRuns = useCallback(() => {
    api.get(`/t/${code}/payment-import/duplicates/runs`, { params: { limit: 5 } }).then((r) => setRuns(r.data || [])).catch(() => {});
  }, [code]);
  useEffect(() => { loadRuns(); }, [loadRuns]);

  const runScan = async (keepResult = false) => {
    setLoading(true); setError(null);
    if (!keepResult) setLastRun(null);
    try {
      const r = await api.get(`/t/${code}/payment-import/duplicates`, { params: { limit: 2000 }, timeout: 120000 });
      setScan(r.data);
      setSelected(new Set((r.data.duplicates || []).map((d) => d.payment_id)));
      if (!r.data.count) toast.success(`No duplicates — ${(r.data.scanned || 0).toLocaleString("en-IN")} imported payment(s) checked`);
      else toast.warning(`${r.data.count} duplicate import cop${r.data.count === 1 ? "y" : "ies"} found — ${fmtINR(r.data.amount)}`);
    } catch (e) { setError(errMsg(e)); } finally { setLoading(false); }
  };

  const rows = scan?.duplicates || [];
  const allChecked = rows.length > 0 && rows.every((d) => selected.has(d.payment_id));
  const picked = useMemo(() => rows.filter((d) => selected.has(d.payment_id)), [rows, selected]);
  const pickedAmount = picked.reduce((n, d) => n + (d.amount || 0), 0);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((d) => d.payment_id)));

  const remove = async () => {
    if (!picked.length) return;
    setRemoving(true);
    try {
      const r = await api.post(`/t/${code}/payment-import/duplicates/remove`,
        { payment_ids: picked.map((d) => d.payment_id), reason: reason.trim() || null, confirm: true }, { timeout: 300000 });
      setLastRun(r.data);
      setConfirmOpen(false);
      setTyped(""); setReason("");
      toast.success(`${r.data.removed} duplicate import cop${r.data.removed === 1 ? "y" : "ies"} removed — ${fmtINR(r.data.amount || 0)} put back on the demands`);
      pokeLiveCollections();   // Phase 149
      loadRuns();
      await runScan(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setRemoving(false); }
  };

  return (
    <>
      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2"><Copy className="h-4 w-4" /> Duplicate imported payments</CardTitle>
          <p className="text-xs text-muted-foreground">
            A duplicate is a bulk-imported payment with the <b>same PIN, date, receipt number and amount</b> as a payment recorded through the app
            (receipt numbers are compared ignoring case, separators and leading zeros). The <b>app record is always kept</b>; only the import copy
            is removed — its money goes back on the demands, the record moves to the recycle bin and every removal is audited.
            New imports and app entries are already blocked from creating duplicates; use this to clean up records that were imported earlier.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" onClick={() => runScan()} disabled={loading} data-testid={`${testId}-scan-button`}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} {scan ? "Scan again" : "Scan for duplicates"}
            </Button>
            {scan && (
              <p className="text-xs text-muted-foreground tabular-nums" data-testid={`${testId}-summary`}>
                {(scan.scanned || 0).toLocaleString("en-IN")} imported payment(s) checked · <b className="text-foreground">{scan.count || 0}</b> duplicate(s) · {fmtINR(scan.amount || 0)}
                {scan.truncated ? " · showing the first 2,000" : ""}
              </p>
            )}
            {rows.length > 0 && (
              <Button type="button" variant="destructive" className="ml-auto" disabled={!picked.length || removing}
                onClick={() => setConfirmOpen(true)} data-testid={`${testId}-remove-button`}>
                <Trash2 className="h-4 w-4 mr-2" /> Remove {picked.length} duplicate cop{picked.length === 1 ? "y" : "ies"}
              </Button>
            )}
          </div>
          {error && (
            <Alert variant="destructive" data-testid={`${testId}-error`}>
              <AlertTriangle className="h-4 w-4" /><AlertTitle>Scan failed</AlertTitle>
              <AlertDescription className="flex items-center justify-between gap-3">{error}
                <Button size="sm" variant="outline" onClick={() => runScan()} data-testid={`${testId}-retry-button`}>Retry</Button>
              </AlertDescription>
            </Alert>
          )}
          {lastRun && (
            <Alert className="border-primary/30 bg-primary/5" data-testid={`${testId}-result`}>
              <CheckCircle2 className="h-4 w-4" /><AlertTitle>Cleanup complete</AlertTitle>
              <AlertDescription className="text-xs">
                {lastRun.removed} duplicate import cop{lastRun.removed === 1 ? "y" : "ies"} removed ({fmtINR(lastRun.amount || 0)}) across {lastRun.properties || 0} propert{lastRun.properties === 1 ? "y" : "ies"}.
                {lastRun.advance_short > 0 && <> {fmtINR(lastRun.advance_short)} of advance credit had already been used and could not be taken back.</>}
                {" "}Removed records are kept in the recycle bin and listed in the audit trail.
              </AlertDescription>
            </Alert>
          )}
          {scan && rows.length === 0 && !error && (
            <div data-testid={`${testId}-empty`}>
              <EmptyState title="No duplicate imports" subtitle="Every bulk-imported payment is unique against the app-entered receipts." />
            </div>
          )}
          {rows.length > 0 && (
            <div className="rounded-md border overflow-x-auto">
              <Table data-testid={`${testId}-table`}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"><Checkbox checked={allChecked} onCheckedChange={toggleAll} aria-label="Select all duplicates" data-testid={`${testId}-select-all`} /></TableHead>
                    <TableHead>PIN</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Import copy (will be removed)</TableHead>
                    <TableHead>App record (kept)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((d) => (
                    <TableRow key={d.payment_id} data-testid={`${testId}-row-${d.payment_id}`} className={selected.has(d.payment_id) ? "bg-destructive/[0.03]" : ""}>
                      <TableCell><Checkbox checked={selected.has(d.payment_id)} onCheckedChange={() => toggle(d.payment_id)} aria-label={`Select ${d.pin}`} data-testid={`${testId}-check-${d.payment_id}`} /></TableCell>
                      <TableCell>
                        <p className="font-mono text-xs">{d.pin || "—"}</p>
                        <p className="text-[11px] text-muted-foreground truncate max-w-[160px]">{d.owner_name || ""}</p>
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(d.payment_date)}</TableCell>
                      <TableCell className="text-right tabular-nums text-xs font-medium">{fmtINR(d.amount)}</TableCell>
                      <TableCell className="text-xs">
                        <p className="font-mono">{d.import_receipt}{d.import_receipt_number && d.import_receipt_number !== d.import_receipt ? <span className="text-muted-foreground font-sans"> (saved as {d.import_receipt_number})</span> : null}</p>
                        <p className="text-[11px] text-muted-foreground">{d.import_mode || "—"} · {d.import_file ? `${d.import_file} · ` : ""}imported {fmtDateTime(d.imported_at)}</p>
                      </TableCell>
                      <TableCell className="text-xs">
                        <p className="font-mono">{d.keeper?.receipt_number || "—"}</p>
                        <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-1">
                          <Badge variant="outline" className="text-[10px] font-normal">{d.keeper?.source === "citizen_portal" ? "Citizen Portal" : (d.keeper?.collection_channel || "app")}</Badge>
                          {d.keeper?.collected_by
                            ? <StaffName code={code} userId={d.keeper.collected_by} name={d.keeper.collected_by_name || "Staff"} empCode={d.keeper.collected_by_employee_id} size="xs" nameClassName="text-[11px]" />
                            : <span>{d.keeper?.collected_by_name || "Online / Citizen"}</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {runs.length > 0 && (
            <div className="text-[11px] text-muted-foreground" data-testid={`${testId}-runs`}>
              <p className="font-medium text-foreground/80 mb-1">Recent cleanup runs</p>
              <ul className="space-y-0.5">
                {runs.map((r) => (
                  <li key={r.id} className="tabular-nums" data-testid={`${testId}-run-${r.id}`}>
                    {fmtDateTime(r.started_at)} · {r.by_name || "—"} · {r.removed} removed ({fmtINR(r.amount || 0)}) of {r.found} found{r.reason ? ` · ${r.reason}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!removing) { setConfirmOpen(o); if (!o) setTyped(""); } }}>
        <DialogContent className="sm:max-w-lg" data-testid={`${testId}-confirm-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trash2 className="h-4 w-4 text-destructive" /> Remove {picked.length} duplicate import cop{picked.length === 1 ? "y" : "ies"}?</DialogTitle>
            <DialogDescription>
              {fmtINR(pickedAmount)} across {picked.length} imported record(s) will be reversed: the money goes back on the demands they had settled,
              advance credit they created is taken back, and the payments, receipts and auto-logged visits move to the recycle bin.
              The matching app-entered receipts are <b>not touched</b>. Every removal is written to the audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor={`${testId}-reason`}>Reason (optional)</Label>
              <Input id={`${testId}-reason`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Legacy file also contained app receipts" maxLength={300} data-testid={`${testId}-reason-input`} />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${testId}-typed`}>Type <span className="font-mono">REMOVE</span> to confirm</Label>
              <Input id={`${testId}-typed`} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" data-testid={`${testId}-typed-input`} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={removing} data-testid={`${testId}-cancel-button`}>Cancel</Button>
            <Button variant="destructive" onClick={remove} disabled={removing || typed.trim().toUpperCase() !== "REMOVE"} data-testid={`${testId}-confirm-button`}>
              {removing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />} Remove duplicates
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PaymentDuplicatesPanel;
